import CoachIntakeNavbar from "@/Components/CoachIntake/CoachIntakeNavbar";
import MobileNavbar from "@/Components/LoggedIn/MobileNavbar/MobileNavbar";
import Footer from "@/Components/Navigation/Footer";
import Navbar from "@/Components/Navigation/Navbar";
import CookieManager from "@/Components/UI/Cookies/CookieManager";
import { DecoyClient } from "@/types/page";
import { sendEventToApp } from "@/utils/appCommunication";
import { useIdentity, useIsIdentityLoaded } from "@/utils/identity";
import { usePresence } from "@/utils/Presence";
import { useMediaQuery } from "@react-hook/media-query";
import { PropsWithChildren, ReactElement, useEffect } from "react";

export type PageContainerProps = PropsWithChildren<{
    title: string;
    allowMobileNavbar?: boolean;
    showFooter?: boolean;
    showFooterOutsideView?: boolean;
    navbarBgColor?: string;
    bottomBgColor?: string;
    footerTopBgColor?: string;
    customFooter?: ReactElement | null;
    hideNavbar?: boolean;
    showICFSponsorBadgeOnFooter?: boolean;
    showSimpleCoachNavbar?: boolean;
    useStrawberryLogo?: boolean;
    navbarTransparentAtTop?: boolean;
    decoyUser?: DecoyClient | null;
}>;

export default function PageContainer({
    children,
    title,
    allowMobileNavbar = false,
    showFooter,
    showFooterOutsideView = false,
    navbarBgColor,
    bottomBgColor,
    footerTopBgColor,
    customFooter = null,
    hideNavbar = false,
    showICFSponsorBadgeOnFooter = true,
    showSimpleCoachNavbar = false,
    useStrawberryLogo = false,
    navbarTransparentAtTop = false,
    decoyUser = null,
}: PageContainerProps): ReactElement {
    const identity = useIdentity();
    const user = decoyUser ?? identity.user;
    const isUserKnown = useIsIdentityLoaded();

    usePresence(identity.user);

    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const isCoachInIntake =
        user?.type === "coach" && !user.completed_onboarding_at;
    const showMobileNavbar =
        allowMobileNavbar && !!user && !isDesktop && !isCoachInIntake;

    const footer =
        (showFooter ?? !user) &&
        (customFooter ?? (
            <Footer
                topBgColor={footerTopBgColor}
                showICFSponsorBadgeOnFooter={showICFSponsorBadgeOnFooter}
            />
        ));

    // The mobile app unregisters the device's push notifications when told the user is null.
    useEffect(() => {
        if (isUserKnown) {
            sendEventToApp("userChanged", user);
        }
    }, [isUserKnown, user]);

    useEffect(() => {
        sendEventToApp("navbarBgColorChanged", {
            navbarBgColor: navbarBgColor ?? "white",
            bottomBgColor: bottomBgColor ?? navbarBgColor ?? "white",
        });
    }, [navbarBgColor, bottomBgColor]);

    return (
        <>
            <title>{title}</title>
            <div className="bg-brand-lightGray text-black/50 print:bg-white">
                <div className="relative flex min-h-dvh flex-col items-center">
                    {!hideNavbar &&
                        !showMobileNavbar &&
                        user?.type !== "coach" && (
                            <Navbar
                                user={user}
                                backgroundColor={navbarBgColor}
                                useStrawberryLogo={useStrawberryLogo}
                                transparentAtTop={navbarTransparentAtTop}
                            />
                        )}
                    {showSimpleCoachNavbar && user?.type === "coach" && (
                        <CoachIntakeNavbar />
                    )}
                    <div
                        id="page-child-container"
                        className="relative flex w-full grow justify-center overflow-y-hidden print:overflow-visible"
                    >
                        {children}
                    </div>
                    <CookieManager />
                    {!hideNavbar && showMobileNavbar && <MobileNavbar />}
                    {!showFooterOutsideView && footer}
                </div>
                {showFooterOutsideView && footer}
            </div>
        </>
    );
}
